/**
 * Usage Analytics › Sourcing (Sofia, Oct 26 2026): how many people the sourcing agent checked and
 * shortlisted, how many were written to, followed up, replied and were hired — per sourcing job and
 * in total — which InMail templates and subjects went out, and what the AI cost.
 *
 * Counts only, never a candidate (`proxy-server/lib/outreachStats.js`). Jobs expire with their
 * candidates after 90 days, so "checked" and "shortlisted" cover live jobs; everything after the
 * send comes from durable records.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '../../../../shared/components/LoadingSpinner';
import { fetchSourcingStats, type SourcingStats } from '../../services/usageApi';
import { StatTile } from './StatTile';
import { RankedBars } from './RankedBars';
import { SERIES_PRIMARY } from './chartTokens';

const RANGES = [30, 90, 365] as const;
const usd = (v: number | null) => (v === null ? '—' : `$${v.toFixed(2)}`);
const pct = (v: number | null) => (v === null ? '—' : `${v}%`);
const day = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/Berlin' });

function Card({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-4">
      <h2 className="m-0 text-sm font-semibold text-zinc-900">{title}</h2>
      {hint && <p className="m-0 mt-0.5 text-xs text-zinc-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Funnel({ s }: { s: SourcingStats['totals'] }) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <StatTile label="Checked" value={String(s.checked)} hint="by the sourcing agent" />
      <StatTile label="Shortlisted" value={String(s.shortlisted)} />
      <StatTile label="InMails sent" value={String(s.sent)} />
      <StatTile label="Follow-ups sent" value={String(s.followUps)} hint={`+ ${s.linkedInFollowUpsDue} scheduled in LinkedIn, now due`} />
      <StatTile label="Replied" value={String(s.replied)} hint={`${pct(s.replyRate)} of sent`} />
      <StatTile label="Hired" value={String(s.hired)} hint={`${pct(s.hireRate)} of sent · from Lever`} />
    </div>
  );
}

export function SourcingAnalytics() {
  const [days, setDays] = useState<(typeof RANGES)[number]>(90);
  const { data, isLoading, error } = useQuery({ queryKey: ['usage-sourcing', days], queryFn: () => fetchSourcingStats(days) });
  // AI cost is recorded since Oct 26 2026; the tab says so instead of showing $0 (Oct 29 2026).
  const since = data?.costs.since ? ` since ${day(data.costs.since)}` : '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-zinc-500">Range</span>
        {RANGES.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setDays(r)}
            className={`rounded-full border px-3 py-1 text-xs ${r === days ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-200 bg-white text-zinc-700'}`}
          >
            Last {r} days
          </button>
        ))}
      </div>
      {isLoading && <LoadingSpinner />}
      {error && <p className="text-sm text-[#B3261E]">{error instanceof Error ? error.message : 'Could not load the sourcing numbers.'}</p>}
      {data && (
        <>
          <Funnel s={data.totals} />
          <div className="grid gap-3 sm:grid-cols-3">
            <StatTile label={`AI cost${since}`} value={usd(data.costs.usd)} hint="screening, checks and drafts of the sourcing jobs" />
            <StatTile label={`AI cost per InMail${since}`} value={usd(data.costs.perSent)} hint="sent to people a sourcing job found" />
            <StatTile label={`AI cost per reply${since}`} value={usd(data.costs.perReply)} />
          </div>
          <Card
            title="Per sourcing job"
            hint={`Newest first: jobs with recorded AI cost or an InMail sent.`}
          >
            {data.jobs.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <thead className="text-xs text-zinc-500">
                    <tr>
                      {['Role', 'Started', 'Checked', 'Shortlisted', 'Sent', 'Follow-ups', 'Replied', 'Hired', `AI cost${since}`, 'Per InMail'].map((h) => (
                        <th key={h} className="py-1 pr-3 font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.jobs.map((j) => (
                      <tr key={j.id} className="border-t border-zinc-100">
                        <td className="py-1.5 pr-3">{j.roleTitle || '—'}</td>
                        <td className="py-1.5 pr-3">{day(j.createdAt)}</td>
                        <td className="py-1.5 pr-3">{j.checked}</td>
                        <td className="py-1.5 pr-3">{j.shortlisted}</td>
                        <td className="py-1.5 pr-3">{j.sent}</td>
                        <td className="py-1.5 pr-3">{j.followUps + j.linkedInFollowUpsDue}</td>
                        <td className="py-1.5 pr-3">
                          {j.replied}
                          {j.replyRate !== null ? ` (${j.replyRate}%)` : ''}
                        </td>
                        <td className="py-1.5 pr-3">{j.hired}</td>
                        <td className="py-1.5 pr-3">{usd(j.costUsd)}</td>
                        <td className="py-1.5 pr-3">{usd(j.costPerSent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="m-0 text-sm text-zinc-500">No sourcing job in this range.</p>
            )}
          </Card>
          <div className="grid gap-3 lg:grid-cols-2">
            <Card title="InMail templates used" hint="Per InMail sent; “AI standard” = no library template.">
              <RankedBars items={data.templates.map((t) => ({ label: t.label, value: t.count }))} color={SERIES_PRIMARY} />
            </Card>
            <Card title="Subjects used" hint="As a pattern; subjects typed by hand or written by the AI are not stored, only counted.">
              <RankedBars items={data.subjects.map((t) => ({ label: t.label, value: t.count }))} color={SERIES_PRIMARY} />
            </Card>
          </div>
          {data.commonGround && data.commonGround.with.sent > 0 && (
            <Card title="Common ground" hint="Reply rate of InMails that opened with something the sender and the person share, and of those that did not.">
              <p className="m-0 text-sm">
                With: <b>{pct(data.commonGround.with.replyRate)}</b> of {data.commonGround.with.sent} · Without: <b>{pct(data.commonGround.without.replyRate)}</b> of{' '}
                {data.commonGround.without.sent}
              </p>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
